import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, ArrowRight } from "lucide-react";
import { kbArticles, kbClusters } from "../data/knowledgeBase";
import { SITE } from "../data/seoPages";

export default function KnowledgeBasePage() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Knowledge Base", item: `${SITE}/knowledge` },
        ],
      },
      {
        "@type": "ItemList",
        name: "BrandBorn Knowledge Base — Marketplace Onboarding & AI Listings",
        itemListElement: kbArticles.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: a.q,
          url: `${SITE}/knowledge/${a.slug}`,
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="knowledge-base-page">
      <Helmet>
        <title>Knowledge Base | Marketplace Onboarding & AI Listing Guides — BrandBorn</title>
        <meta name="description" content="Expert answers on marketplace onboarding, AI listing software, catalog management and quick commerce. By BrandBorn — India's #1 onboarding agency." />
        <link rel="canonical" href={`${SITE}/knowledge`} />
        <meta property="og:title" content="BrandBorn Knowledge Base — Marketplace & AI Listing Guides" />
        <meta property="og:description" content="Expert answers on marketplace onboarding, AI listings, catalog management and quick commerce." />
        <meta property="og:url" content={`${SITE}/knowledge`} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="px-4 py-14 md:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-100 text-red-600 rounded-full text-xs font-bold mb-5">
            <BookOpen size={14} /> KNOWLEDGE BASE
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            Marketplace & AI Listing Knowledge Base
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Clear, expert answers on marketplace onboarding, AI listings, catalog management, analytics and quick commerce — from the team behind 1000+ brand launches.
          </p>
        </div>
      </section>

      {kbClusters.map((cluster) => (
        <section key={cluster} className="px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-black text-gray-900 mb-5">{cluster}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {kbArticles.filter((a) => a.cluster === cluster).map((a) => (
                <Link key={a.slug} to={`/knowledge/${a.slug}`} className="group flex items-center justify-between gap-3 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-red-200 transition-all" data-testid={`kb-card-${a.slug}`}>
                  <h3 className="font-bold text-gray-900 text-sm">{a.q}</h3>
                  <ArrowRight size={16} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="px-4 py-10">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-3 text-sm font-bold">
          <Link to="/software" className="text-blue-600 hover:underline">AI Software →</Link>
          <Link to="/services" className="text-blue-600 hover:underline">Onboarding Services →</Link>
          <Link to="/blog" className="text-blue-600 hover:underline">Blog →</Link>
          <Link to="/faq" className="text-blue-600 hover:underline">FAQs →</Link>
        </div>
      </section>
    </div>
  );
}
